import React from 'react';
import * as common from '@/common';
import {
  Coord,
} from '@t/LayoutTypes';
import {
  BScanData,
  ImageRegistrationTransform,
} from '@t/ImagingTypes';
import {
  octSettings,
  octBoundsDeg,
} from '@/imageFiles';
import { BlobImageSet } from '@/cache';
import { CroppedImage } from '@svg/CroppedImage';

export const strokeWidthDeg = 0.05;

export interface LocData extends Coord {
  id: number;
  arrayID: string;
  diameter: number;
  isMarked?: boolean;
  fill?: string;
  stroke?: string;
}

export interface MarginData extends Coord {
  arrayID: string;
  vertical: boolean;
  stroke: string;
}

export function getFoveaDeg(fovea: BScanData, octPx: number): Coord | undefined {
  if (!fovea)
    return undefined;
  return {
    x: common.lerp(fovea.scanX, 0, octPx, octBoundsDeg.xMin, octBoundsDeg.xMax),
    y: common.lerp(fovea.scanIndex, 0, fovea.totalScans - 1, octBoundsDeg.yMax, octBoundsDeg.yMin),
  };
}

export function getEnfaceDeg(bscanSlice: number, bscanIndex: number): Coord {
  return {
    x: common.lerp1(bscanSlice, octBoundsDeg.xMin, octBoundsDeg.xMax),
    y: common.lerp(bscanIndex, 0, octSettings.totalBscans - 1, octBoundsDeg.yMax, octBoundsDeg.yMin),
  };
}

export function renderLoc(loc: LocData) {
  if (loc.fill && loc.stroke)
    return <circle key={loc.id} cx={loc.x} cy={loc.y} r={loc.diameter/2} fill={loc.fill} stroke={loc.stroke} />
  return <circle key={loc.id} cx={loc.x} cy={loc.y} r={loc.diameter/2} />
}

export function renderMargin(m: MarginData) {
  const deg = 2;
  const half = deg / 2;
  const x = m.vertical ? 0 : half;
  const y = m.vertical ? half : 0;
  return <line key={m.arrayID} x1={m.x - x} y1={m.y - y} x2={m.x + x} y2={m.y + y} stroke={m.stroke} />
}

export function renderMark(loc: LocData) {
  const r = loc.diameter / 2;
  return <g key={'x '+loc.id}>
    <line key={`x1 ${loc.id}`} x1={loc.x-r} y1={loc.y-r} x2={loc.x+r} y2={loc.y+r} />
    <line key={`x2 ${loc.id}`} x1={loc.x+r} y1={loc.y-r} x2={loc.x-r} y2={loc.y+r} />
  </g>
}

export function renderCropped(url: string, crop) {
  return <CroppedImage url={url} x={crop.x} y={crop.y} w={crop.width} h={crop.height} />
}

export function renderFoveal(c: Coord) {
  return <g opacity={0.67} transform={`translate(${c.x}, ${c.y})`}>
    <line key='fovx' x1={0} y1={-0.5} x2={0} y2={0.5} strokeWidth={strokeWidthDeg} stroke='yellow' />,
    <line key='fovy' x1={-0.5} y1={0} x2={0.5} y2={0} strokeWidth={strokeWidthDeg} stroke='yellow' />,
  </g>
}

export function renderFoveaCircle(fovDeg: Coord, color: string): JSX.Element {
  return <g opacity={0.5} fill='transparent' stroke={color} strokeWidth={0.1} transform={`translate(${fovDeg.x}, ${fovDeg.y})`}>
    <line x1={-1} y1={0} x2={1} y2={0} />
    <line x1={0} y1={-1} x2={0} y2={1} />
    <circle r={2.5} />
  </g>
}

export function renderScanBox(): JSX.Element {
  return <rect
    x={octBoundsDeg.xMin}
    y={octBoundsDeg.yMin}
    width={octSettings.patternSize.w}
    height={octSettings.patternSize.h}
    fill='transparent'
    stroke='#0F0'
    strokeWidth={0.1}
    opacity={0.5}
  />
}

export function makeRegTransform(reg, children) {
  return <g transform={transformAttr(reg)}>
    {children}
  </g>
}

export function transformAttr(reg: ImageRegistrationTransform) {
  return `translate(${reg.translation.x}, ${reg.translation.y}) rotate(${reg.rotation}) scale(${reg.scale})`;
}

export function lineH(length: number, color: string) {
  return <line x1={-length/2} y1={0} x2={length/2} y2={0} strokeWidth={strokeWidthDeg} stroke={color} />
}

export function lineV(length: number, color: string) {
  return <line x1={0} y1={-length/2} x2={0} y2={length/2} strokeWidth={strokeWidthDeg} stroke={color} />
}